// src/components/Layout.tsx

import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import {
  ShoppingCart,
  BarChart2,
  Settings,
  LogOut,
  LayoutDashboard,
  User,
  Menu,
  X,
  Users,
  PackagePlus
} from 'lucide-react';
import { authService } from '../services/authService';
import './Layout.css';

interface LayoutProps {
  children: React.ReactNode;
}

interface MenuItem {
  path: string;
  label: string;
  icon: any;
  roles: string[]; // Roles that can access this menu item
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [username, setUsername] = useState<string | null>(null);

  useEffect(() => {
    // Get user role and username from JWT token
    const role = authService.getUserRole();
    const user = authService.getUsername();
    setUserRole(role);
    setUsername(user);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/login');
  };

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const closeSidebar = () => {
    setIsSidebarOpen(false);
  };

  // Define all menu items with their allowed roles
  const allMenuItems: MenuItem[] = [
    { path: '/', label: 'Panel Principal', icon: LayoutDashboard, roles: ['Admin', 'Cashier'] },
    { path: '/sales', label: 'Ventas', icon: ShoppingCart, roles: ['Admin', 'Cashier'] },
    { path: '/customer-management', label: 'Gestión de Clientes', icon: User, roles: ['Admin', 'Cashier'] },
    { path: '/product-management', label: 'Gestión de Productos', icon: PackagePlus, roles: ['Admin'] },
    { path: '/category-management', label: 'Gestión de Categorías', icon: PackagePlus, roles: ['Admin'] },
    { path: '/user-management', label: 'Gestión de Usuarios', icon: Users, roles: ['Admin'] },
    { path: '/user-register', label: 'Registro de Usuarios', icon: User, roles: ['Admin'] },
    { path: '/reports', label: 'Reportes de Ventas', icon: BarChart2, roles: ['Admin'] },
    { path: '/inventory-reports', label: 'Reportes de Inventario', icon: BarChart2, roles: ['Admin'] },
    { path: '/settings', label: 'Configuración', icon: Settings, roles: ['Admin'] },
  ];

  // Filter menu items based on user role
  const menuItems = userRole
    ? allMenuItems.filter(item => item.roles.includes(userRole))
    : [];

  const getPageTitle = () => {
    const currentItem = menuItems.find(item => item.path === location.pathname);
    return currentItem ? currentItem.label : 'Sistema POS';
  };

  return (
    <div className="main-container">
      {/* Mobile Overlay */}
      {isSidebarOpen && (
        <div className="sidebar-overlay" onClick={closeSidebar}></div>
      )}

      {/* Sidebar */}
      <div className={`sidebar-container ${isSidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <div className="brand-logo">
            <ShoppingCart size={24} className="text-primary-400" />
            POSDynamic
          </div>
          <button className="close-sidebar-btn" onClick={closeSidebar}>
            <X size={24} />
          </button>
        </div>

        <nav className="sidebar-menu">
          {menuItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) =>
                `menu-item ${isActive ? 'active' : ''}`
              }
              onClick={closeSidebar}
            >
              <item.icon size={20} />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="sidebar-footer">
          <button onClick={handleLogout} className="logout-button">
            <LogOut size={18} />
            Cerrar Sesión
          </button>
          <div className="copyright">
            © 2025 POSDynamic v1.0
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="content-area">
        <header className="header">
          <div className="header-left">
            <button className="hamburger-btn" onClick={toggleSidebar}>
              <Menu size={24} />
            </button>
            <div className="header-title">
              <h1>{getPageTitle()}</h1>
            </div>
          </div>
          <div className="user-info">
            <div className="user-avatar">
              <User size={18} />
            </div>
            <span>{username || 'Usuario'} ({userRole || 'N/A'})</span>
          </div>
        </header>

        <main className="main-content">
          {children}
        </main>
      </div>
    </div>
  );
};

export default Layout;